import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { IStripeConnectAccount, Organization } from '../models';
import { OrganizationService } from './organization.service';
import { UserService } from './user.service';

const STORAGE_KEY_PREFIX = 'et.stripeAccountKey.';

/**
 * Tracks which Stripe Connect account the current user is viewing in Client Invoicing.
 * - Admins/owners can switch between accounts (selection persisted per org).
 * - Trainers assigned to an account are locked to that account.
 */
@Injectable({
  providedIn: 'root'
})
export class StripeAccountContextService {

  private accountsSubject = new BehaviorSubject<IStripeConnectAccount[]>([]);
  private selectedKeySubject = new BehaviorSubject<string>('default');
  private loadedOrgId: string = null;

  public accounts$: Observable<IStripeConnectAccount[]> = this.accountsSubject.asObservable();
  public selectedKey$: Observable<string> = this.selectedKeySubject.asObservable();

  constructor(
    private organizationService: OrganizationService,
    private userService: UserService,
  ) {}

  get accounts(): IStripeConnectAccount[] {
    return this.accountsSubject.value;
  }

  get selectedKey(): string {
    return this.selectedKeySubject.value;
  }

  get selectedAccount(): IStripeConnectAccount | null {
    return this.accounts.find((a) => a.key === this.selectedKey) || null;
  }

  get enabledAccounts(): IStripeConnectAccount[] {
    return this.accounts.filter((a) => a.stripeStatus === 'enabled');
  }

  /** Accounts the current user is allowed to switch between / view. */
  get viewableAccounts(): IStripeConnectAccount[] {
    const org = this.organizationService.getAppOrganization();
    const user = this.userService.getAppUser();
    if (!org || !user) {
      return [];
    }
    if (org.isUserAdmin(user.uid) || org.isUserOwner(user.uid)) {
      return this.accounts;
    }
    const assigned = this.getAssignedAccountForUser(user.uid);
    return assigned ? [assigned] : [];
  }

  canSwitchAccounts(): boolean {
    const org = this.organizationService.getAppOrganization();
    const user = this.userService.getAppUser();
    if (!org || !user) {
      return false;
    }
    return (org.isUserAdmin(user.uid) || org.isUserOwner(user.uid)) && this.viewableAccounts.length > 1;
  }

  /** Admin/owner OR trainer with an assigned (enabled) Stripe account. */
  canAccessInvoicingAdmin(): boolean {
    const org = this.organizationService.getAppOrganization();
    const user = this.userService.getAppUser();
    if (!org || !user) {
      return false;
    }
    if (org.isUserAdmin(user.uid) || org.isUserOwner(user.uid)) {
      return true;
    }
    const assigned = this.getAssignedAccountForUser(user.uid);
    return !!assigned && assigned.stripeStatus === 'enabled';
  }

  getAssignedAccountForUser(userId: string): IStripeConnectAccount | null {
    return this.accounts.find((a) => !a.isDefault && a.trainerId === userId) || null;
  }

  async ensureLoaded(force = false): Promise<IStripeConnectAccount[]> {
    const org = this.organizationService.getAppOrganization();
    if (!org?.uid) {
      return [];
    }
    if (!force && this.loadedOrgId === org.uid && this.accounts.length > 0) {
      return this.accounts;
    }
    return this.reload(org);
  }

  async reload(org?: Organization): Promise<IStripeConnectAccount[]> {
    const organization = org || this.organizationService.getAppOrganization();
    if (!organization?.uid) {
      this.accountsSubject.next([]);
      this.selectedKeySubject.next('default');
      this.loadedOrgId = null;
      return [];
    }

    let accounts: IStripeConnectAccount[] = [];
    try {
      const res = await this.organizationService.getOrganizationStripeAccounts(organization.uid);
      accounts = this.normalizeAccountsResponse(res);
    } catch (error) {
      console.log(error);
      accounts = [];
    }

    accounts = await this.ensureDefaultAccount(organization, accounts);
    this.applyOrgTitleToDefaultAccount(organization, accounts);
    this.accountsSubject.next(accounts);
    this.loadedOrgId = organization.uid;
    this.applyDefaultSelection(organization, accounts);
    return accounts;
  }

  /** Organization name for the default Connect account. Never show "Default". */
  displayLabel(account?: { label?: string; isDefault?: boolean; key?: string } | null): string {
    const label = (account?.label || '').trim();
    const genericDefault = /^default$/i.test(label);
    const isDefaultAccount = !!(account?.isDefault || account?.key === 'default' || genericDefault);
    if (isDefaultAccount) {
      const org = this.organizationService.getAppOrganization();
      const orgTitle = (org?.title || org?.name || '').trim();
      if (orgTitle) {
        return orgTitle;
      }
    }
    return label;
  }

  /** Show the org name for the default Connect account instead of "Default". */
  private applyOrgTitleToDefaultAccount(organization: Organization, accounts: IStripeConnectAccount[]) {
    for (const account of accounts) {
      account.label = this.displayLabel(account) || account.label;
    }
  }

  /** Coerce API payload to an array (guards against unexpected object shapes). */
  private normalizeAccountsResponse(res: any): IStripeConnectAccount[] {
    if (Array.isArray(res)) {
      return res;
    }
    if (res && typeof res === 'object') {
      return Object.keys(res).map((key) => {
        const value = res[key];
        if (value && typeof value === 'object') {
          return { key: value.key || key, ...value } as IStripeConnectAccount;
        }
        return null;
      }).filter(Boolean);
    }
    return [];
  }

  /**
   * If the accounts list is missing a usable default (common when /accounts isn't
   * deployed yet, or legacy orgs), build one from the org Connect account.
   */
  private async ensureDefaultAccount(
    organization: Organization,
    accounts: IStripeConnectAccount[]
  ): Promise<IStripeConnectAccount[]> {
    const existingDefault = accounts.find((a) => a.isDefault || a.key === 'default');
    if (existingDefault?.stripeAccount) {
      // Keep status in sync with org when API returns an incomplete stub
      if (!existingDefault.stripeStatus && organization.stripeStatus) {
        existingDefault.stripeStatus = organization.stripeStatus;
      }
      return accounts;
    }

    if (!organization.stripeStatus) {
      return accounts;
    }

    let stripeAccountId = existingDefault?.stripeAccount || '';
    let stripeStatus: 'enabled' | 'incomplete' = organization.stripeStatus || 'incomplete';

    try {
      const account: any = await this.organizationService.getStripeAccount(organization.uid);
      stripeAccountId = account?.id || account?.stripeAccount || stripeAccountId;
      if (account?.status === 'enabled' || account?.status === 'incomplete') {
        stripeStatus = account.status;
      } else if (account?.charges_enabled && account?.payouts_enabled) {
        stripeStatus = 'enabled';
      }
    } catch (error) {
      console.log(error);
    }

    if (!stripeAccountId && !organization.stripeStatus) {
      return accounts;
    }

    const defaultAccount: IStripeConnectAccount = {
      key: 'default',
      label: organization.title || organization.name || existingDefault?.label || 'Default',
      isDefault: true,
      stripeAccount: stripeAccountId || '',
      stripeStatus,
      trainerId: existingDefault?.trainerId,
      stripeTaxesEnabled: existingDefault?.stripeTaxesEnabled,
      stripeOneTimeFeeProduct: existingDefault?.stripeOneTimeFeeProduct,
    };

    const others = accounts.filter((a) => !a.isDefault && a.key !== 'default');
    return [defaultAccount, ...others];
  }

  selectAccount(accountKey: string) {
    const org = this.organizationService.getAppOrganization();
    const user = this.userService.getAppUser();
    if (!org || !user) {
      return;
    }

    // Trainers cannot switch away from their assigned account
    if (!org.isUserAdmin(user.uid) && !org.isUserOwner(user.uid)) {
      const assigned = this.getAssignedAccountForUser(user.uid);
      if (assigned) {
        this.selectedKeySubject.next(assigned.key);
        return;
      }
    }

    const exists = this.accounts.some((a) => a.key === accountKey);
    if (!exists) {
      return;
    }
    this.selectedKeySubject.next(accountKey);
    try {
      localStorage.setItem(STORAGE_KEY_PREFIX + org.uid, accountKey);
    } catch (e) {
      // ignore storage failures
    }
  }

  private applyDefaultSelection(org: Organization, accounts: IStripeConnectAccount[]) {
    const user = this.userService.getAppUser();
    if (!user) {
      this.selectedKeySubject.next('default');
      return;
    }

    // Assigned trainers are locked to their account
    if (!org.isUserAdmin(user.uid) && !org.isUserOwner(user.uid)) {
      const assigned = accounts.find((a) => !a.isDefault && a.trainerId === user.uid);
      if (assigned) {
        this.selectedKeySubject.next(assigned.key);
        return;
      }
    }

    let stored: string = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY_PREFIX + org.uid);
    } catch (e) {
      stored = null;
    }

    if (stored && accounts.some((a) => a.key === stored)) {
      this.selectedKeySubject.next(stored);
      return;
    }

    const defaultAccount = accounts.find((a) => a.isDefault) || accounts[0];
    this.selectedKeySubject.next(defaultAccount?.key || 'default');
  }
}
